import { ClientSideErrorEvent, FlowErrorMetadata, IdFlowFailedEvent } from "../utils/events"
import { ERROR_NAMES, ErrorName } from "./errors"

export const errorToResponseBodyErrorMessage = async (error: unknown): Promise<string> => {
    if (isResponseError(error)) {
        return (await error.response.clone().json()).errors?.[0]?.message ?? error.message
    }

    if (isSlashIDError(error)) {
        if (!error.context.response?.body) {
            return error.message
        }

        try {
            const body = JSON.parse(error.context.response?.body)
            return body.errors?.[0]?.message ?? error.message
        } catch {
            return error.message
        }
    }

    if (error instanceof Error) {
        return error.message
    }

    return ""
}

const errorToResponseStatusCode = (error: any): number | undefined => {
    if (isResponseError(error)) {
        return error.response.status
    }

    if (isSlashIDError(error)) {
        return error.context.response?.status
    }

    return error.response?.status
}

export type ResponseError = Error & {
    name: "ResponseError"
    response: Response
}

export function isResponseError(error: any): error is ResponseError {
    return error.name === "ResponseError" && error.response instanceof Response
}

/**
 * Headers are case-insensitive
 */
export const RESPONSE_HEADERS = {
    SlashIDCorrelationID: "slashid-correlation-id",
} as const

export interface SerialisedResponseError {
    status: Response["status"]
    statusText: Response["statusText"]
    url: Response["url"]
    type: Response["type"]
    ok: Response["ok"]
    redirected: Response["redirected"]
    headers: Record<string, string>
    body: Awaited<ReturnType<Response["json"]>>
}

export const serialiseResponseError = async (error: ResponseError): Promise<SerialisedResponseError> => {
    const data = {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.response.url,
        type: error.response.type,
        ok: error.response.ok,
        redirected: error.response.redirected,
        headers: Object.fromEntries(error.response.headers),
        body: JSON.stringify(await error.response.clone().json(), null, 2),
    }

    return data
}

/**
 * This error happens when an existing user that has no reachable handle tries to authenticate without previously setting a password.
 */
export async function isNoPasswordSetError(error: ResponseError): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 403 && message === "no password set"
}

/**
 * Happens during registration when sign-up settings -> "Disallow users from signing-up"
 */
export async function isSelfRegistrationNotAllowedError(error: ResponseError): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 403 && message === "self-registration not allowed for this organization"
}

export async function isHandlePatternNotAllowedError(error: any): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 403 && message === "person cannot be registered"
}

export async function isSignUpAwaitingApprovalError(error: any): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 403 && message === "person is not active"
}

export async function isSignInAwaitingApprovalError(error: any): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    // the SDK doesn't have person ID to match
    return status === 403 && message.includes("Person") && message.includes("is inactive")
}

export function isRateLimitError(error: any): boolean {
    const status = errorToResponseStatusCode(error)

    return status === 429
}

export async function isTimeoutError(error: any): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return (status === 401 || status === 403) && message === "authentication has expired"
}

export async function isRegistrationAlreadyCompleteError(error: any): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 409 && message === "person exists"
}

export async function isInvalidEmailAddressFormat(error: ResponseError): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 400 && message.startsWith("malformed e-mail address")
}

export async function isInvalidPhoneNumberFormat(error: ResponseError): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 400 && message.startsWith("malformed phone number")
}

export async function isUnreachablePhoneNumber(error: ResponseError): Promise<boolean> {
    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 400 && message.startsWith("unreachable phone number")
}

export async function isBadPasskeyScopeError(error: any): Promise<boolean> {
    const regexp = /selected scope '(.*?)' is not equal to or a registrable domain suffix of origin '(.*?)'/

    const status = errorToResponseStatusCode(error)
    const message = await errorToResponseBodyErrorMessage(error)

    return status === 400 && regexp.test(message)
}

export async function isPasskeyPromptError(error: any): Promise<boolean> {
    const chromeRegexp =
        /The operation either timed out or was not allowed. See: https:\/\/www.w3.org\/TR\/webauthn-2\/#sctn-privacy-considerations-client./

    const safariRegexp = /This request has been cancelled by the user./
    const firefoxRegexp = /The operation was aborted./
    const iosRegExp = /Operation failed./
    const notAvailableRegexp = /The WebAuthn API is not available in the current environment/
    const credentialManagerRegexp = /An unknown error occurred while talking to the credential manager./

    const message = await errorToResponseBodyErrorMessage(error)

    return (
        error instanceof Error &&
        (chromeRegexp.test(message) ||
            safariRegexp.test(message) ||
            firefoxRegexp.test(message) ||
            iosRegExp.test(message) ||
            notAvailableRegexp.test(message) ||
            credentialManagerRegexp.test(message))
    )
}

export function isThrownResponse(response: any): response is ApiErrorResponsePayload {
    return (
        typeof response === "object" &&
        Array.isArray(response.errors) &&
        response.errors.every((error: ApiErrorObject) => {
            return typeof error.httpcode === "number" && typeof error.message === "string"
        })
    )
}

export function isTimeoutInPayload(response: ApiErrorResponsePayload): boolean {
    return response.errors.some((error) => error.message === "authentication has expired")
}

export function isRateLimitInPayload(response: ApiErrorResponsePayload): boolean {
    return response.errors.some((error) => error.httpcode === 429)
}

export async function isOIDCPopupBlockedError(error: any): Promise<boolean> {
    const regex = /Popup blocked, please consider redirect instead/
    const message = await errorToResponseBodyErrorMessage(error)

    return error instanceof DOMException && regex.test(message)
}

/**
 * Based on MDN: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#checking_that_the_fetch_was_successful
 */
export function isNetworkError(error: unknown, response?: Response): boolean {
    if (error instanceof TypeError && !response) {
        return true
    }

    return false
}

export type ApiErrorObject = {
    httpcode: number
    message: string
}

export type ApiErrorResponsePayload = {
    errors: ApiErrorObject[]
}

export const CLIENT_GENERATED_ERROR_SLUGS = [
    // these slugs are generated from events
    "incorrect_otp_code_via_email",
    "incorrect_otp_code_via_sms",
    "incorrect_otp_code",
    "invalid_password", // password that does not meet the validation rules
    "incorrect_password", // password that does not match the password stored for the given handle
    "recovery_failed", // account recovery method rejected
    "oidc_popup_blocked",
    "client_side_error",
    "unspecified",
    // these slugs are generated from SlashIDError instances
    "no_password_set",
    "flow_cancelled",
    "handle_pattern_not_allowed",
    "sign_up_awaiting_approval",
    "sign_in_awaiting_approval",
    "self_registration_not_allowed",
    "recover_non_reachable_handle_type",
    "timeout",
    "rate_limit_exceeded",
    "passkey_bad_scope",
    "passkey_prompt_failed",
    "api_response_error",
    "invalid_slashid_configuration",
    "invalid_slashid_url_params",
    "invalid_authentication_method",
    "invalid_action",
    "invalid_email_address_format",
    "invalid_phone_number_format",
    "unreachable_phone_number", // country code blocked by Twilio
    "person_exists",
] as const

export type ClientGeneratedErrorSlug = (typeof CLIENT_GENERATED_ERROR_SLUGS)[number]

// ensures that we don't miss adding a new SlashIDError name to the map
export const ERROR_NAMES_TO_SLUGS_MAP: Record<ErrorName, ClientGeneratedErrorSlug> = {
    noPasswordSet: "no_password_set",
    UnexpectedError: "unspecified",
    FlowCancelledError: "flow_cancelled",
    HandlePatternNotAllowedError: "handle_pattern_not_allowed",
    SignUpAwaitingApprovalError: "sign_up_awaiting_approval",
    SignInAwaitingApprovalError: "sign_in_awaiting_approval",
    SelfRegistrationNotAllowed: "self_registration_not_allowed",
    recoverNonReachableHandleType: "recover_non_reachable_handle_type",
    TimeoutError: "timeout",
    RateLimitError: "rate_limit_exceeded",
    BadPasskeyScopeError: "passkey_bad_scope",
    PasskeyPromptError: "passkey_prompt_failed",
    APIResponseError: "api_response_error",
    InvalidSlashIDConfigurationError: "invalid_slashid_configuration",
    InvalidSlashIDURLParamsError: "invalid_slashid_url_params",
    InvalidAuthenticationMethodError: "invalid_authentication_method",
    InvalidActionError: "invalid_action",
    InvalidEmailAddressFormatError: "invalid_email_address_format",
    InvalidPhoneNumberFormatError: "invalid_phone_number_format",
    UnreachablePhoneNumberError: "unreachable_phone_number",
    PersonExistsError: "person_exists",
} as const

export type SlashIDErrorContext = Record<string, any> & { response?: SerialisedResponseError }

export type SlashIDInternalErrorInput = {
    name: ErrorName
    message: string
    context?: SlashIDErrorContext
    // the nested error that caused the issue
    cause?: Error
}

export class SlashIDError extends Error {
    name: ErrorName
    context: SlashIDErrorContext

    constructor({
        name = ERROR_NAMES.unknown,
        message = "An error occured in @slashid/slashid",
        context = {},
        cause,
    }: SlashIDInternalErrorInput) {
        super(message, { cause })
        this.name = name
        this.context = context
    }
}

export function isSlashIDError(error: any): error is SlashIDError {
    return error instanceof SlashIDError
}

/**
 * Narrows down the ResponseError to a specific SlashIDError type
 */
export async function createSlashIDAPIResponseError(
    responseError: ResponseError,
    context?: SlashIDErrorContext | undefined
): Promise<SlashIDError> {
    // specific to webauthn
    if (await isBadPasskeyScopeError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.badPasskeyScopeError,
            message: "Passkey scope is invalid",
            cause: responseError,
        })
    }

    if (isRateLimitError(responseError)) {
        const rateLimitMessage = await errorToResponseBodyErrorMessage(responseError)
        const serialisedResponse = await serialiseResponseError(responseError)

        return new SlashIDError({
            name: ERROR_NAMES.rateLimitError,
            message: rateLimitMessage,
            cause: responseError,
            context: {
                response: serialisedResponse,
                ...context,
            },
        })
    }

    if (await isNoPasswordSetError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.noPasswordSet,
            message: responseError.message,
            context: {
                ...context,
            },
        })
    }

    if (await isSelfRegistrationNotAllowedError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.selfRegistrationNotAllowed,
            message: responseError.message,
            context: {
                ...context,
            },
        })
    }

    if (await isHandlePatternNotAllowedError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.handlePatternNotAllowed,
            message: responseError.message,
            context: {
                ...context,
            },
        })
    }

    if (await isSignUpAwaitingApprovalError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.signUpAwaitingApproval,
            message: responseError.message,
            context: {
                ...context,
            },
        })
    }

    if (await isSignInAwaitingApprovalError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.signInAwaitingApproval,
            message: responseError.message,
            context: {
                ...context,
            },
        })
    }

    if (await isTimeoutError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.timeoutError,
            message: "Authentication has expired",
            cause: responseError,
        })
    }

    if (await isBadPasskeyScopeError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.badPasskeyScopeError,
            message: "Passkey scope is invalid",
            cause: responseError,
        })
    }

    if (await isRegistrationAlreadyCompleteError(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.personExistsError,
            message: "Person with the given handle already exists",
            cause: responseError,
        })
    }

    if (await isInvalidEmailAddressFormat(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.invalidEmailAddressFormatError,
            message: "Email address is malformed",
            cause: responseError,
        })
    }

    if (await isInvalidPhoneNumberFormat(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.invalidPhoneNumberFormatError,
            message: "Phone number is malformed",
            cause: responseError,
        })
    }

    if (await isUnreachablePhoneNumber(responseError)) {
        return new SlashIDError({
            name: ERROR_NAMES.unreachablePhoneNumberError,
            message: "Phone number is unreachable",
            cause: responseError,
        })
    }

    // catch-all, in case nothing was matched
    const response = await serialiseResponseError(responseError)
    return new SlashIDError({
        name: ERROR_NAMES.apiResponseError,
        message: "An error occured in @slashid/slashid while processing the response",
        context: { response, correlationId: response.headers[RESPONSE_HEADERS.SlashIDCorrelationID] },
        cause: responseError,
    })
}

/**
 * Accepts any value, derivative of Error or otherwise, and produces a SlashIDError
 */
export async function toSlashIDError(value: unknown): Promise<SlashIDError> {
    if (value instanceof SlashIDError) {
        return value
    }

    if (value instanceof Error) {
        // webauthn specific errors
        if (await isPasskeyPromptError(value)) {
            return new SlashIDError({
                name: ERROR_NAMES.passkeyPromptError,
                message: "Passkey prompt was dismissed",
                cause: value,
            })
        }

        if (isResponseError(value)) {
            return await createSlashIDAPIResponseError(value)
        }

        return new SlashIDError({
            name: ERROR_NAMES.unknown,
            message: `Unknown error: ${value.message}`,
            cause: value,
        })
    }

    // in some cases the response body is thrown instead of the error or the full response object
    if (isThrownResponse(value)) {
        // special case for thrown responses with a timeout error in the payload
        if (isTimeoutInPayload(value)) {
            return new SlashIDError({
                name: ERROR_NAMES.timeoutError,
                message: "Authentication has expired",
                context: { thrownResponse: value },
            })
        }

        // special case for thrown responses with a rate limit error in the payload
        if (isRateLimitInPayload(value)) {
            return new SlashIDError({
                message: "Rate limit exceeded",
                context: { thrownResponse: value },
                name: ERROR_NAMES.rateLimitError,
            })
        }

        return new SlashIDError({
            message: "An error occured in @slashid/slashid",
            context: { thrownResponse: value },
            name: ERROR_NAMES.apiResponseError,
        })
    }

    let stringified = "[Unable to stringify the thrown value]"
    try {
        stringified = JSON.stringify(value)
    } catch {
        // ignore
    }

    const error = new Error(`Non-Error thrown: ${stringified}`)
    return new SlashIDError({
        name: ERROR_NAMES.unknown,
        message: error.message,
        cause: error,
    })
}

/**
 * Ensure that a valid error instance is returned, since in JS you can throw anything.
 * @deprecated Use toSlashIDError(value: unknown)
 */
export function ensureError(value: unknown): Error {
    if (value instanceof Error) {
        return value
    }

    let stringified = "[Unable to stringify the thrown value]"
    try {
        stringified = JSON.stringify(value)
    } catch {
        // ignore
    }

    const error = new Error(`Non-Error thrown: ${stringified}`)
    return error
}

export async function getErrorMessage(error: unknown) {
    const normalisedError = isSlashIDError(error) ? error : await toSlashIDError(error)

    return normalisedError.message
}

// TODO fix this - make custom error types so we don't have to add them to the switch
export async function createEventFromError(
    error: unknown,
    { authenticationFactor, handle, previousToken, challengeId }: Omit<FlowErrorMetadata, "failureDetail">
): Promise<IdFlowFailedEvent | ClientSideErrorEvent> {
    const slashIDError = await toSlashIDError(error)
    const errorSlug = ERROR_NAMES_TO_SLUGS_MAP[slashIDError.name]

    const event: IdFlowFailedEvent | ClientSideErrorEvent = {
        authenticationFactor,
        handle,
        errorText: errorSlug || "client_side_error",
        previousToken,
        challengeId,
        failureDetail: await getErrorMessage(error),
    }

    return event
}
